import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { ErrorHandlerService } from './error-handler/error-handler.service';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '../../environments/environment';
import { BehaviorSubject, catchError, finalize, take, tap } from 'rxjs';
import { ModalContentService } from './modal-content/modal-content.service';
import { ModalContentData } from './modal-content/modal-content';
import { ModalContentTypes } from './constants/modal-content-types';
import { SignalsStoreService } from './signals-store.service';
import { LocalStorageService } from './local-storage.service';
import { LOCALSTORAGE_KEYS } from './constants/databases';
import { MODAL_PROSPECT_TITLE } from './modal/contents/prospect.constants';
import { RequestHandlerParams } from './types/api-service.types';
import { ApiService } from './api.service';

export interface NavegoAddressResponse {
  city: string;
  clientNr: string;
  cutoffDay: string;
  cutoffTime: string;
  deliveryDay: string;
  deliveryWindow: string;
  latitude: number;
  longitude: number;
  state: string;
  street: string;
  zoneId: number;
  zoneName: string;
}
export interface NavegoAddressRequest {
  street: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  zipCode: string;
}

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private modalContentService = inject(ModalContentService);
  private signalsStoreService = inject(SignalsStoreService);
  private localStorageService = inject(LocalStorageService);
  #apiService = inject(ApiService);

  private setFirstPickUpMembershipSelectedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  setFirstPickUpMembershipSelected$ = this.setFirstPickUpMembershipSelectedSubject.asObservable();


  endpoints = {
    checkZipCode: `/zipcode/validate`
  }

  isLoading: WritableSignal<boolean> = signal(false);
  navegoAddress: WritableSignal<NavegoAddressResponse | undefined> = signal(undefined);

  constructor(private http: HttpClient) { }

  checkDeliveryAddressByZipCode(addressInfo: NavegoAddressRequest, openModalProspect: boolean = true, anonymousUser: boolean = false, loading?: WritableSignal<boolean>) {

    this.navegoAddress.set(undefined)
    this.isLoading.set(true);

    if (!loading) {
      if (!addressInfo.zipCode) return this.signalsStoreService.addressWithoutZipCode.set(addressInfo);
      else this.signalsStoreService.addressWithoutZipCode.set(null);
    }

    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: `${this.endpoints.checkZipCode}/${addressInfo.zipCode}`,
      apiV3: true,
      showErrorMessage: false,
      returnError: true
    };

    this.#apiService.handleRequest(params).pipe(
      tap((response: any) => {
        if (response.error) {
          loading?.set(false);

          if (anonymousUser)
            this.localStorageService.remove(LOCALSTORAGE_KEYS.ANONYMOUS_ADDRESS);

          if (openModalProspect)
            this.openModalProspect();
          return;
        }

        const info = { ...addressInfo, ...response };

        this.navegoAddress.set(info)

        if (!this.navegoAddress())
          loading?.set(false);

        if (anonymousUser)
          this.setUpAnonymousUserAddress(info);

      }),
      finalize(() => {
        this.isLoading.set(false);
      })
    ).subscribe();
  }

  setupDeliveryData(place: google.maps.places.PlaceResult) {
    let city = '';
    let stateCode = '';
    let zipCode = '';
    let latitude: number | (() => number) | undefined = place.geometry?.location?.lat
    let longitude: number | (() => number) | undefined = place?.geometry?.location?.lng
    if (typeof latitude == 'function') latitude = latitude();
    if (typeof longitude == 'function') longitude = longitude();
    let streetNameArray = ['', ''];

    let locality, vicinity, neighborhood;

    vicinity = place?.vicinity;

    for (const element of place.address_components || []) {
      for (let j = 0; j < element.types.length; j++) {

        if (element.types[j] === 'locality') {
          locality = element.long_name;
          continue;
        }

        if (element.types[j] === 'neighborhood' && !city) {
          neighborhood = element.long_name;
          continue;
        }

        if (element.types[j] === 'administrative_area_level_1') {
          stateCode = element.short_name;
          continue;
        }

        if (element.types[j] === 'postal_code') {
          zipCode = element.long_name;
          continue;
        }

        if (element.types[j] === 'street_number') {
          streetNameArray[0] = element.short_name;
          continue;
        }

        if (element.types[j] === 'route') {
          streetNameArray[1] = element.short_name;
          continue;
        }

      }
    }

    city = locality || vicinity || neighborhood || '-';

    let formattedAddress = place.formatted_address;
    const name = streetNameArray.join(' ').trim() || place.name;

    return {
      city,
      stateCode,
      zipCode,
      latitude,
      longitude,
      formattedAddress,
      name,
      placeId: place.place_id
    }
  }

  private openModalProspect() {
    const modalRef = this.modalContentService.openModal(
      ModalContentTypes.PROSPECT,
      {
        title: MODAL_PROSPECT_TITLE
      }
    );
    modalRef.closed.subscribe((result) => console.log(result))
  }

  private setUpAnonymousUserAddress(res: any) {
    const googleAddress = this.signalsStoreService.googleAddress();
    const addressInfo = {
      locationText: googleAddress?.street,
      ...googleAddress,
      // Keep the zone the zipcode lookup resolved. `res` was already being
      // handed to this method and thrown away, so the delivery day, window and
      // cut-off were re-fetched later from scratch. The explorer flow
      // (DEV-11262) needs `zoneId` to build `POST /explorer`, and this is the
      // only point where it is known without asking the server again.
      zoneId: res?.zoneId,
      zoneName: res?.zoneName,
      deliveryDay: res?.deliveryDay,
      deliveryWindow: res?.deliveryWindow,
      cutoffDay: res?.cutoffDay,
      cutoffTime: res?.cutoffTime,
    }
    this.localStorageService.set(LOCALSTORAGE_KEYS.ANONYMOUS_ADDRESS, { ...addressInfo });
    this.signalsStoreService.anonymousUserAddress.set({ ...addressInfo });
  }

  setFirstPickUpMembershipSelected() {
    this.setFirstPickUpMembershipSelectedSubject.next(true);
  }

}
